import type { BaseQueryFn } from "@reduxjs/toolkit/query";

import { getConfig, isServerSide } from "@jti/fe-common";
import type { Sdk as MagentoApiSdk } from "@jti/magento-api-v2";
import { MagentoApiClientV2 } from "@jti/magento-api-v2";

type FunctionKeys<T> = {
  [K in keyof T]: T[K] extends (...args: any[]) => any ? K : never;
}[keyof T];

type MagentoApiSdkMethod = FunctionKeys<MagentoApiSdk>;

// DO NOT EXPORT MagentoV2QueryArgsWithMethod
// exporting this type causes TS to fail building declarations when infering type for query endpoint hooks.
type MagentoV2QueryArgsWithMethod = {
  [M in MagentoApiSdkMethod]: {
    httpMethod?: "GET" | "POST";
    method: M;
    params?: Parameters<MagentoApiSdk[M]>;
  };
}[MagentoApiSdkMethod];

const deriveIsTrialFromState = (state: unknown): boolean => {
  if (!state || typeof state !== "object") {
    return false;
  }

  return false; // TODO: Implement trial state derivation logic based on the actual shape of the state when trial state is needed for Magento V2 requests.
};

export const magentoBaseQuery: BaseQueryFn<MagentoV2QueryArgsWithMethod, unknown, unknown> = async (
  { httpMethod, method, params = [] },
  api
) => {
  const isTrial = deriveIsTrialFromState(api.getState());
  const magentoApiClient = MagentoApiClientV2.init(isServerSide() ? getConfig() : undefined);
  const sdk = magentoApiClient.request({ isTrial, method: httpMethod });
  const methodToCall = sdk[method];

  if (typeof methodToCall !== "function") {
    throw new Error(`Method '${String(method)}' does not exist on Magento V2 SDK.`);
  }

  const signal = api.type === "query" ? api.signal : undefined;

  try {
    const [variables, requestHeaders] = params;
    const result = await (methodToCall as (...args: unknown[]) => Promise<unknown>)(variables, requestHeaders, signal);
    return { data: result };
  } catch (error: unknown) {
    return { error };
  }
};
